import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api, getToken, setToken } from '../lib/api';
import { ErrorNotice } from '../components/ui';
import { Brand } from '../components/chrome';
import { LoginBackdrop } from '../components/LoginBackdrop';
import {
  IconArrow,
  IconBook,
  IconChart,
  IconEye,
  IconEyeOff,
  IconGear,
  IconLock,
  IconMail,
  IconShield,
  IconStethoscope,
  IconTablet,
} from '../components/icons';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (getToken()) return <Navigate to="/" replace />;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ token: string }>('/auth/login', { email, password });
      setToken(result.token);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-hero">
      <div className="login-bg" aria-hidden>
        <span className="orb o1" />
        <span className="orb o3" />
      </div>
      <LoginBackdrop />

      <header className="login-top">
        <Brand />
        <p className="login-tagline">Secure · Reliable · For a Brighter Tomorrow</p>
      </header>

      <div className="login-grid">
        <section className="login-promo">
          <h2 className="promo-title">Powering Fair Exams for a Brighter Future</h2>
          <p className="lead">Secure device management for high-stakes exams like NEET, JEE and more.</p>
          <ul className="promo-list">
            <li>
              <span className="promo-icon blue"><IconShield /></span>
              Secure &amp; Controlled Exam Environment
            </li>
            <li>
              <span className="promo-icon navy"><IconTablet /></span>
              Manage Multiple Exam Tablets
            </li>
            <li>
              <span className="promo-icon slate"><IconChart /></span>
              Real-time Monitoring &amp; Compliance
            </li>
          </ul>
          <blockquote>Technology for a fairer, brighter tomorrow.</blockquote>
        </section>

        <aside className="login-tablet">
          <span className="login-tablet-cam" />
          <div className="login-panel">
            <div className="login-head">
              <p className="login-role">Administrator</p>
              <h1>Exam Tablet Management</h1>
              <p className="sub">Use your administrator account to manage enrolled devices.</p>
            </div>

            <ErrorNotice error={error} />

            <form className="login-form" onSubmit={submit}>
              <label>
                <span>Email</span>
                <div className="field-icon">
                  <IconMail />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    placeholder="Enter your email"
                    required
                    autoFocus
                  />
                </div>
              </label>

              <label>
                <span>Password</span>
                <div className="field-icon">
                  <IconLock />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    className="eye-btn"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
              </label>

              <button className="primary login-submit" type="submit" disabled={busy}>
                <span>{busy ? 'Signing in…' : 'Sign in'}</span>
                <IconArrow />
              </button>
            </form>

            <p className="small muted lock-note">
              Accounts lock for 15 minutes after 5 failed attempts.
            </p>

            <div className="trust-row">
              <span>Trusted for National Level Exams</span>
            </div>
            <div className="exam-pills">
              <span><IconStethoscope /> NEET</span>
              <span><IconGear /> JEE</span>
              <span><IconBook /> and more…</span>
            </div>
          </div>
        </aside>
      </div>

      <p className="login-foot">Learn · Practice · Achieve</p>
    </div>
  );
}
